import { get } from "helpers/api_helper"
import { getClientListPagedUrl, getClientUpcompingActivitiesAPI, normalizeLeadPagedResponse } from "helpers/backend_helper"
import queryClient from "helpers/queryClient"
import { leadQueryOptions } from "../Lead/dashboard/leadsUtils"

const agentKey = () => String(localStorage.getItem("userId") || "")

export const fetchClientPage = (agentId, pageNumber, pageSize) => {
  const id = parseInt(agentId, 10)
  return queryClient.fetchQuery(
    ["clients", "page", id, pageNumber, pageSize],
    () =>
      getClientListPagedUrl({
        agentId: id,
        pageNumber,
        pageSize,
        sortBy: "createdDate",
        sortDirection: "desc",
        filterColumns: [],
        filterValues: [],
      }).then(normalizeLeadPagedResponse),
    leadQueryOptions
  )
}

export const clientPageKey = (agentId, pageNumber, pageSize) => ["clients", "page", parseInt(agentId, 10), pageNumber, pageSize]

export const fetchUpcomingActivities = agentId =>
  queryClient.fetchQuery(["clients", "upcoming", agentKey()], () => getClientUpcompingActivitiesAPI(agentId), leadQueryOptions)

export const upcomingKey = () => ["clients", "upcoming", agentKey()]

export const fetchClientStats = () => {
  const year = new Date().getFullYear()
  return queryClient.fetchQuery(
    ["clients", "stats", agentKey(), year],
    () =>
      get("Client/getClientCountDashboardByAgent", { params: { agentId: agentKey(), year } }).then(response => {
        const rows = Array.isArray(response) ? response : []
        const count = key => rows.find(item => item.sources === key)?.counts ?? 0
        return { total: count("Total"), closings: count("Month"), conversion: count("ClientConversion") }
      }),
    leadQueryOptions
  )
}

export const statsKey = () => ["clients", "stats", agentKey(), new Date().getFullYear()]

export const fetchClientSources = period =>
  queryClient.fetchQuery(
    ["clients", "sources", agentKey(), String(period)],
    () =>
      get(withPeriod(`Client/getClientSourceDashboardByAgentMonths?agentId=${agentKey()}`, period)).then(response =>
        (Array.isArray(response) ? response : [])
          .map(item => ({ label: item.sources || "Unknown", count: parseInt(item.counts, 10) || 0 }))
          .filter(item => item.count > 0)
          .sort((a, b) => b.count - a.count)
      ),
    leadQueryOptions
  )

export const sourcesKey = period => ["clients", "sources", agentKey(), String(period)]

export const fetchClientMonthly = period => {
  const year = new Date().getFullYear()
  return queryClient.fetchQuery(
    ["clients", "monthly", agentKey(), year, String(period)],
    () =>
      get(withPeriod(`Client/clientCitiesMonthlySalesByOfficeMonths?agentId=${agentKey()}&year=${year}`, period)).then(response => {
        const months = response?.months || []
        const counts = response?.counts || []
        return months.map((label, index) => ({ label, count: Number(counts[index]) || 0 }))
      }),
    leadQueryOptions
  )
}

export const monthlyKey = period => ["clients", "monthly", agentKey(), new Date().getFullYear(), String(period)]

export const fetchClientCities = period =>
  queryClient.fetchQuery(
    ["clients", "cities", agentKey(), String(period)],
    () =>
      get(withPeriod(`Client/clientCitiesDistributionByOfficeMonths?agentId=${agentKey()}`, period)).then(response => {
        const cities = response?.cities || []
        const percentages = response?.percentages || []
        const counts = response?.counts || []
        let noCityPercent = 0
        const items = []
        cities.forEach((city, index) => {
          const percent = Number(percentages[index]) || 0
          if (String(city || "").trim()) items.push({ label: city, percent, count: counts[index] })
          else noCityPercent += percent
        })
        items.sort((a, b) => b.percent - a.percent)
        return { items, noCityPercent }
      }),
    leadQueryOptions
  )

export const citiesKey = period => ["clients", "cities", agentKey(), String(period)]

export const fetchClientNotifications = () =>
  queryClient.fetchQuery(
    ["dashboard", "client-activity", agentKey()],
    () => get("Notification/allAgent", { params: { ForId: agentKey() } }).then(res => (Array.isArray(res) ? res : [])),
    leadQueryOptions
  )

export const notificationsKey = () => ["dashboard", "client-activity", agentKey()]

export function dropClientCache(key) {
  return queryClient.removeQueries(key, { exact: true })
}

export function cachedClientGet(key, request) {
  return queryClient.fetchQuery(key, request, leadQueryOptions)
}

export const clientDetailKey = id => ["clients", "detail", String(id)]
export const clientMessageKey = clientId => ["clients", "messages", String(clientId), agentKey()]
export const clientDocumentKey = clientId => ["clients", "documents", String(clientId), agentKey()]
export const clientAppointmentKey = (agentId, clientId) => ["clients", "appointments", String(agentId), String(clientId)]
export const clientNoteKey = (agentId, clientId) => ["clients", "notes", String(agentId), String(clientId)]
export const clientShowingKey = clientId => ["clients", "seller-showings", String(clientId), agentKey()]
export const clientSellerClosingKey = clientId => ["clients", "seller-closings", String(clientId), agentKey()]
export const clientSellerListingKey = clientId => ["clients", "seller-listings", String(clientId), agentKey()]
export const clientOpenHouseKey = clientId => ["clients", "open-houses", String(clientId), agentKey()]
export const clientBuyerListingKey = (clientId, agentId) => ["clients", "buyer-listings", String(clientId), String(agentId)]
export const clientImportantDateKey = clientId => ["clients", "important-dates", String(clientId), agentKey()]
export const clientFamilyKey = clientId => ["clients", "family", String(clientId), agentKey()]
export const clientBuyerClosingKey = clientId => ["clients", "buyer-closings", String(clientId), agentKey()]
export const clientDealsKey = (agentId, clientId) => ["clients", "deals", String(agentId), String(clientId)]
export const clientEngagementKey = (clientId, agentId, typeId, statusId) => ["clients", "engagement", String(clientId), String(agentId), String(typeId), String(statusId)]

export function agentProfileReady(userdetail) {
  const userId = agentKey()
  if (!userId || !userdetail) return false
  return String(userdetail.agentId ?? userdetail.id) === userId
}

const withPeriod = (url, period) => (period !== "" && period != null ? `${url}&months=${encodeURIComponent(period)}` : url)
